import React, { useCallback, useState, useEffect } from 'react';
import {
    ActivityData,
    APNPartnerPrivateCustomer,
} from '../../../../interfaces/activity';
import {
    getSelectedActivity,
    selectIsLoading,
    selectSelectedActivity,
    updateSelectedActivity,
} from '../../../../store/slices/selectedActivitySlice';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Button, Form } from '@amzn/awsui-components-react-v2';
import { useNotifications } from '../../../../../common/context/grimsbyNotifications';

import useFormValidation from '../../../../../common/utils/formValidation';
import { saveCustomerValidationConfig } from '../../../../services/activity-service';
import { APNCustomerFormSection } from './APNCustomerFormSection';
import {
    isSemiPrivateActivity,
    validateClassSizeConsistency,
} from '../../Common/Common';
import { validateClassroomCommitmentsCap } from '../../Common/classroomEligibilityRules';
import { handleScrollToError } from '../../../../../common/utils/scrollToError';

const APNCustomerEditForm = () => {
    const isLoading = useSelector(selectIsLoading);
    const match = useRouteMatch<{ id: string; customerId: any }>();
    const history = useHistory();
    const dispatch = useDispatch();
    const activity: ActivityData | undefined = useSelector(
        selectSelectedActivity,
    )?.activity;
    const { addNotification } = useNotifications();
    const [classSizeValidationError, setClassSizeValidationError] =
        useState<string>('');
    const initialFormState = {
        customer_name: '',
    } as APNPartnerPrivateCustomer;

    const [formValues, setFormValues] =
        useState<APNPartnerPrivateCustomer>(initialFormState);

    const { errors, validateForm } =
        useFormValidation<APNPartnerPrivateCustomer>();

    const handleFieldEvent = useCallback(
        (changes: Partial<APNPartnerPrivateCustomer>) => {
            // Clear class size validation error when user modifies the field
            if ('number_of_students_committed' in changes) {
                setClassSizeValidationError('');
            }
            setFormValues((values) => ({
                ...values,
                ...changes,
            }));
        },
        [],
    );

    useEffect(() => {
        if (activity && activity.customers) {
            setFormValues(activity.customers[match.params.customerId]);
        }
    }, [match.params.customerId, activity]);

    useEffect(() => {
        dispatch(getSelectedActivity(match.params.id));
    }, [dispatch, match.params.id]);

    const handleCreateCustomer = async () => {
        if (!!activity) {
            const invalid = validateForm(
                formValues,
                saveCustomerValidationConfig,
            );

            if (!invalid) {
                let updatedCustomers: Array<APNPartnerPrivateCustomer> = [
                    ...activity.customers,
                ];

                const customerToEdit: APNPartnerPrivateCustomer = {
                    ...formValues,
                };

                updatedCustomers[match.params.customerId] = customerToEdit;

                // For semi-private activities, auto-update class_size to match sum of customer commitments
                const updatedActivity = {
                    ...activity,
                    customers: updatedCustomers,
                };
                const validation =
                    validateClassSizeConsistency(updatedActivity);
                // Auto-sync class_size for semi-private activities
                if (isSemiPrivateActivity(activity)) {
                    updatedActivity.class_size = validation.sum;
                }

                const commitmentsCapError = validateClassroomCommitmentsCap(
                    activity,
                    updatedCustomers,
                );
                if (commitmentsCapError) {
                    setClassSizeValidationError(commitmentsCapError);
                    setTimeout(() => handleScrollToError(), 0);
                    return;
                }

                const result = await dispatch<any>(
                    updateSelectedActivity(match.params.id, updatedActivity),
                );

                addNotification({
                    id: `edit-activity-customer-${Date.now()}`,
                    ...(result.isSuccessful
                        ? {
                              type: 'success',
                              content: 'The customer has been saved.',
                          }
                        : {
                              type: 'error',
                              content:
                                  'An error occurred while saving the customer.',
                          }),
                });

                if (result.isSuccessful) {
                    history.push({
                        pathname: `/activities/${match.params.id}`,
                        search: 'tabId=customers',
                    });
                }
            }
        }
    };

    return (
        <section data-testid="CustomerCreateForm">
            <Form
                header="Edit customer"
                actions={
                    <div className="awsui-util-action-stripe awsui-util-mb-m">
                        <div className="awsui-util-action-stripe-group">
                            <Button
                                variant="link"
                                className="admin-customer-cancel"
                                data-testid="CustomerCreateFormCancel"
                                onClick={() => {
                                    history.push({
                                        pathname: `/activities/${match.params.id}`,
                                        search: 'tabId=customers',
                                    });
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                className="admin-customer-save"
                                data-testid="CustomerCreateFormAddButton"
                                loading={isLoading}
                                onClick={() => {
                                    handleCreateCustomer();
                                }}
                            >
                                {`${isLoading ? 'Saving' : 'Save'}`}
                            </Button>
                        </div>
                    </div>
                }
            >
                <APNCustomerFormSection
                    formValues={formValues}
                    errors={{
                        ...errors,
                        number_of_students_committed:
                            classSizeValidationError ||
                            errors?.number_of_students_committed,
                    }}
                    handleFieldEvent={handleFieldEvent}
                    activity={activity}
                />
            </Form>
        </section>
    );
};

export default APNCustomerEditForm;
